import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  ArrowLeft,
  ArrowRight,
  Upload,
  X,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Wrench,
  Camera,
  FileText,
  Image,
  ListChecks,
  Users,
  UserCheck
} from "lucide-react";

const EQUIPMENT_CATEGORIES = [
  "Kitchen Equipment", "HVAC", "Plumbing", "Electrical", 
  "Lighting", "Upholstery", "Bathroom", "Structural", "Other"
];

const STEPS = [
  { id: 1, name: "Location", icon: FileText },
  { id: 2, name: "Equipment", icon: Wrench },
  { id: 3, name: "Details & Photos", icon: Camera },
  { id: 4, name: "Troubleshooting", icon: ListChecks },
];

export default function NewTicket() {
  const { unitId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);

  const [formData, setFormData] = useState({
    restaurant_name: "",
    equipment_category: "",
    brand_model: "",
    serial_number: "",
    description: "",
    actions_attempted: "",
  });

  const [photos, setPhotos] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [restaurants, setRestaurants] = useState([]);

  // Escalation picker state
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [marketTechs, setMarketTechs] = useState([]);
  const [opsUsers, setOpsUsers] = useState([]);
  const [selectedTechEmail, setSelectedTechEmail] = useState("");
  const [selectedOpsUserId, setSelectedOpsUserId] = useState("");
  const [escalatePick, setEscalatePick] = useState(""); // deprecated — remove once no refs

  // Purchase-Research gate (opens BEFORE Escalate dialog)
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseLinks, setPurchaseLinks] = useState([{ url: "", note: "" }]);
  const [purchaseNewPhotos, setPurchaseNewPhotos] = useState([]);
  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false);

  // Fetch restaurants for this market
  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const res = await api.get(`/markets/${encodeURIComponent(unitId)}/restaurants`);
        setRestaurants(res.data.restaurants || []);
      } catch {
        setRestaurants([]);
      }
    };
    if (unitId) fetchRestaurants();
  }, [unitId]);

  // Fetch market techs AND operations users (DOs/DMs) for the escalation picker
  useEffect(() => {
    const fetchAssignables = async () => {
      try {
        const res = await api.get(`/markets/${encodeURIComponent(unitId)}/technicians`);
        setMarketTechs(res.data.technicians || []);
      } catch {
        setMarketTechs([]);
      }
      try {
        const res = await api.get(`/markets/${encodeURIComponent(unitId)}/operations-users`);
        setOpsUsers(res.data.operations_users || []);
      } catch {
        setOpsUsers([]);
      }
    };
    if (unitId) fetchAssignables();
  }, [unitId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 10) {
      toast.error("Maximum 10 photos allowed");
      return;
    }

    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    setPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const fetchChecklist = useCallback(async (category) => {
    setChecklistLoading(true);
    try {
      const res = await api.get(`/checklists/${encodeURIComponent(category)}`);
      const items = res.data.items.map((item, index) => ({
        id: index,
        text: item,
        checked: false,
        status: null // null, "attempted", "not_applicable"
      }));
      setChecklist(items);
      setChecklistItems(items);
    } catch (error) {
      toast.error("Failed to load checklist");
    } finally {
      setChecklistLoading(false);
    }
  }, []);

  useEffect(() => {
    if (step === 4 && formData.equipment_category) {
      fetchChecklist(formData.equipment_category);
    }
  }, [step, formData.equipment_category, fetchChecklist]);

  const handleChecklistChange = (index, field, value) => {
    setChecklistItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.restaurant_name) {
          toast.error("Please select a restaurant");
          return false;
        }
        return true;
      case 2:
        if (!formData.equipment_category) {
          toast.error("Please select an equipment category");
          return false;
        }
        return true;
      case 3:
        if (!formData.description) {
          toast.error("Please describe the problem");
          return false;
        }
        if (photos.length < 3) {
          toast.error("Please upload at least 3 photos");
          return false;
        }
        return true;
      case 4:
        const allAddressed = checklistItems.every(item => item.status !== null);
        if (!allAddressed) {
          toast.error("Please address all checklist items");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const createTicket = async () => {
    setSubmitting(true);
    try {
      // Create ticket
      const ticketRes = await api.post(`/tickets?unit_id=${unitId}`, formData);
      const newTicketId = ticketRes.data.id;
      setTicketId(newTicketId);

      // Upload photos
      if (photos.length > 0) {
        try {
          const photoFormData = new FormData();
          photos.forEach(p => photoFormData.append("files", p.file));
          await api.post(`/tickets/${newTicketId}/photos`, photoFormData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
        } catch (photoErr) {
          // Ticket was created successfully but photos failed.
          // Surface a soft warning and continue to step 4 - photos can be re-uploaded later.
          const detail = photoErr.response?.data?.detail || photoErr.message || "Unknown error";
          toast.error(`Ticket created (${ticketRes.data.job_number || ""}) but photo upload failed: ${detail}. You can retry from the ticket detail page.`);
          setStep(4);
          return;
        }
      }

      toast.success(`Ticket ${ticketRes.data.job_number || "created"} - moving to troubleshooting`);
      setStep(4);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to create ticket: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    if (step === 3) {
      await createTicket();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleBack = () => {
    setStep(s => Math.max(1, s - 1));
  };

  const handleTroubleshootingComplete = async (resolved, assigneeEmail = null) => {
    if (!ticketId) return;

    setSubmitting(true);
    try {
      await api.post(`/tickets/${ticketId}/troubleshooting`, {
        checklist_items: checklistItems.map(item => ({
          text: item.text,
          status: item.status
        })),
        resolved,
        assignee_email: assigneeEmail || null,
      });

      if (resolved) {
        toast.success("Issue resolved! Ticket closed.");
      } else if (assigneeEmail) {
        toast.success(`Ticket assigned to ${assigneeEmail}`);
      } else {
        toast.success("Ticket made available to all market technicians");
      }
      setEscalateDialogOpen(false);
      navigate(`/ticket/${ticketId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete troubleshooting");
    } finally {
      setSubmitting(false);
    }
  };

  const openEscalateDialog = () => {
    // New flow: open the Items-Needed (Purchase Research) gate FIRST.
    // Submitting (or skipping) it opens the Escalate dialog.
    setSelectedTechEmail("");
    setSelectedOpsUserId("");
    setEscalatePick("");
    setPurchaseLinks([{ url: "", note: "" }]);
    setPurchaseNewPhotos([]);
    setPurchaseDialogOpen(true);
  };

  // Save purchase details, then open the real Escalate dialog.
  // `sourcedByTechnician=true` skips the link/photo requirement.
  const handleSubmitPurchaseDetails = async (sourcedByTechnician = false) => {
    if (!ticketId) return;
    const cleanedLinks = purchaseLinks
      .map((l) => ({ url: (l.url || "").trim(), note: (l.note || "").trim() }))
      .filter((l) => l.url);
    if (
      !sourcedByTechnician &&
      cleanedLinks.length === 0 &&
      purchaseNewPhotos.length === 0
    ) {
      toast.error("Add at least one shopping-cart link or upload at least one picture.");
      return;
    }
    setPurchaseSubmitting(true);
    try {
      if (!sourcedByTechnician && purchaseNewPhotos.length > 0) {
        const fd = new FormData();
        purchaseNewPhotos.forEach((p) => fd.append("files", p));
        await api.post(`/tickets/${ticketId}/photos?stage=purchase_details`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      await api.post(`/tickets/${ticketId}/purchase-details`, {
        links: sourcedByTechnician ? [] : cleanedLinks,
        sourced_by_technician: sourcedByTechnician,
      });
      toast.success(
        sourcedByTechnician
          ? "Technician will source the items — pick an assignee next."
          : "Purchase details saved — pick an assignee next."
      );
      setPurchaseDialogOpen(false);
      setEscalateDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save purchase details");
    } finally {
      setPurchaseSubmitting(false);
    }
  };

  // Unified picker submit: no longer used — the Escalate dialog now has two
  // separate dropdowns with their own submit handlers. Kept intentionally
  // empty so any stale import doesn't break; delete once nothing references it.

  const progress = (step / STEPS.length) * 100;

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar unitId={unitId} />
      
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">New Ticket</h1>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isComplete = step > s.id;
              
              return (
                <div
                  key={s.id}
                  className={`flex items-center gap-2 ${
                    isActive ? "text-indigo-600" : isComplete ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isActive ? "bg-indigo-100" : isComplete ? "bg-green-100" : "bg-gray-100"
                  }`}>
                    {isComplete ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">{s.name}</span>
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Content */}
        <Card className="bg-white border-gray-200 max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900">
              {STEPS[step - 1].name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Step 1: Location */}
            {step === 1 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <Label htmlFor="restaurant_name">Restaurant Name *</Label>
                  {restaurants.length > 0 ? (
                    <Select
                      value={formData.restaurant_name}
                      onValueChange={(v) => handleChange("restaurant_name", v)}
                    >
                      <SelectTrigger data-testid="restaurant-name-input">
                        <SelectValue placeholder="Select restaurant..." />
                      </SelectTrigger>
                      <SelectContent>
                        {restaurants.map((name) => (
                          <SelectItem key={name} value={name}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      No restaurants configured for this market. Ask your admin to add restaurants in Market Config.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Equipment */}
            {step === 2 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <Label>Equipment Category *</Label>
                  <Select
                    value={formData.equipment_category}
                    onValueChange={(v) => handleChange("equipment_category", v)}
                  >
                    <SelectTrigger data-testid="equipment-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {EQUIPMENT_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brand_model">Brand / Model (Optional)</Label>
                  <Input
                    id="brand_model"
                    placeholder="e.g., True Refrigerator TWT-48"
                    value={formData.brand_model}
                    onChange={(e) => handleChange("brand_model", e.target.value)}
                    data-testid="brand-model-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serial_number">Serial Number (Optional)</Label>
                  <Input
                    id="serial_number"
                    placeholder="e.g., SN123456"
                    value={formData.serial_number}
                    onChange={(e) => handleChange("serial_number", e.target.value)}
                    data-testid="serial-number-input"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Details & Photos */}
            {step === 3 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <Label htmlFor="description">Problem Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe the issue in detail..."
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    rows={4}
                    data-testid="description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="actions_attempted">Actions Already Attempted</Label>
                  <Textarea
                    id="actions_attempted"
                    placeholder="What have you already tried?"
                    value={formData.actions_attempted}
                    onChange={(e) => handleChange("actions_attempted", e.target.value)}
                    rows={3}
                    data-testid="actions-attempted-input"
                  />
                </div>

                {/* Photo Upload */}
                <div className="space-y-2">
                  <Label>Photos * (Minimum 3)</Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="photo-upload"
                      data-testid="photo-upload-input"
                    />
                    <label
                      htmlFor="photo-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Upload className="w-10 h-10 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">
                        Click to upload or drag and drop
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        PNG, JPG up to 10MB each
                      </span>
                    </label>
                  </div>

                  {/* Photo Previews */}
                  {photos.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-4">
                      {photos.map((photo, index) => (
                        <div key={photo.preview || `photo-${index}`} className="photo-preview">
                          <img src={photo.preview} alt={`Upload ${index + 1}`} />
                          <button
                            onClick={() => removePhoto(index)}
                            className="remove-btn"
                            type="button"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className={`text-sm ${photos.length >= 3 ? "text-green-600" : "text-gray-500"}`}>
                    {photos.length} / 3 minimum photos uploaded
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Troubleshooting */}
            {step === 4 && (
              <div className="space-y-4 animate-fade-in">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Troubleshooting Required</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Please attempt each troubleshooting step below before escalating. Mark each item as "Attempted" or "Not Applicable".
                      </p>
                    </div>
                  </div>
                </div>

                {checklistLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {checklistItems.map((item, index) => (
                      <div
                        key={item.id}
                        className={`p-4 rounded-lg border ${
                          item.status === "attempted" ? "bg-green-50 border-green-200" :
                          item.status === "not_applicable" ? "bg-gray-50 border-gray-200" :
                          "bg-white border-gray-200"
                        }`}
                      >
                        <p className="text-gray-900 mb-3">{item.text}</p>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={item.status === "attempted"}
                              onCheckedChange={(checked) => 
                                handleChecklistChange(index, "status", checked ? "attempted" : null)
                              }
                              data-testid={`checklist-attempted-${index}`}
                            />
                            <span className="text-sm text-gray-700">Attempted</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={item.status === "not_applicable"}
                              onCheckedChange={(checked) => 
                                handleChecklistChange(index, "status", checked ? "not_applicable" : null)
                              }
                              data-testid={`checklist-na-${index}`}
                            />
                            <span className="text-sm text-gray-700">Not Applicable</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Troubleshooting Actions */}
                <div className="pt-6 border-t border-gray-200 space-y-3">
                  <Button
                    onClick={() => handleTroubleshootingComplete(true)}
                    disabled={submitting || !checklistItems.every(i => i.status)}
                    className="w-full bg-green-600 hover:bg-green-700 h-12"
                    data-testid="issue-resolved-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Issue Resolved - Close Ticket
                  </Button>
                  <Button
                    onClick={openEscalateDialog}
                    disabled={submitting || !checklistItems.every(i => i.status)}
                    variant="outline"
                    className="w-full h-12 border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="escalate-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Issue Not Resolved - Escalate to Help Desk
                  </Button>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            {step < 4 && (
              <div className="flex justify-between mt-8 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={step === 1 || submitting}
                  data-testid="back-step-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={submitting}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="next-step-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Creating...
                    </span>
                  ) : step === 3 ? (
                    <>Create Ticket</>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* ======== Items Needed to Fix (pre-escalation) ======== */}
      <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden" data-testid="purchase-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
            <DialogTitle className="flex items-center gap-2 text-amber-900 text-lg">
              <FileText className="w-5 h-5" />
              Items Needed to Fix the Issue
            </DialogTitle>
            <p className="text-sm text-amber-800/90 mt-1">
              Before escalating, upload a <strong>link + a picture</strong> of
              the shopping cart for every item needed to fix this issue.
            </p>
            <div className="mt-3 text-xs text-amber-900/90 bg-white/70 border border-amber-200 rounded-md px-3 py-2 leading-relaxed">
              <strong>Tip:</strong> paste the problem description along with the
              equipment <em>brand/model</em> and <em>serial number</em> into
              <strong> Microsoft Copilot</strong> or <strong>ChatGPT</strong> —
              ask it to find the exact replacement part on Amazon, Grainger, or
              the manufacturer's site, then drop the link below and attach a
              screenshot of the cart.
            </div>
          </DialogHeader>
          <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-gray-800">
                  Shopping-cart link(s) for the needed items
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setPurchaseLinks((prev) => [...prev, { url: "", note: "" }])}
                  className="h-7 text-xs"
                  data-testid="purchase-add-link-btn"
                >
                  + Add link
                </Button>
              </div>
              <div className="space-y-2.5">
                {purchaseLinks.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2">
                    <Input
                      value={row.url}
                      onChange={(e) =>
                        setPurchaseLinks((prev) =>
                          prev.map((r, i) => (i === idx ? { ...r, url: e.target.value } : r))
                        )
                      }
                      placeholder="https://..."
                      className="col-span-6"
                      data-testid={`purchase-link-url-${idx}`}
                    />
                    <Input
                      value={row.note}
                      onChange={(e) =>
                        setPurchaseLinks((prev) =>
                          prev.map((r, i) => (i === idx ? { ...r, note: e.target.value } : r))
                        )
                      }
                      placeholder="Short note (part, qty…)"
                      className="col-span-5"
                      data-testid={`purchase-link-note-${idx}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() =>
                        setPurchaseLinks((prev) =>
                          prev.length === 1 ? [{ url: "", note: "" }] : prev.filter((_, i) => i !== idx)
                        )
                      }
                      className="col-span-1 text-gray-400 hover:text-red-600 hover:bg-red-50 px-2"
                      data-testid={`purchase-link-remove-${idx}`}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-800">
                Picture of the shopping cart / needed item
              </Label>
              <p className="text-xs text-gray-500">
                Attach a screenshot of the Amazon/Grainger cart and/or a clear
                photo of the broken part so the technician arrives prepared.
              </p>
              <Input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setPurchaseNewPhotos((prev) => [...prev, ...files]);
                  e.target.value = "";
                }}
                className="cursor-pointer"
                data-testid="purchase-photos-input"
              />
              {purchaseNewPhotos.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {purchaseNewPhotos.map((f, i) => (
                    <span
                      key={`${f.name}-${i}`}
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200"
                    >
                      <Image className="w-3 h-3" />
                      {f.name}
                      <button
                        type="button"
                        onClick={() => setPurchaseNewPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                        className="ml-0.5 text-indigo-500 hover:text-red-600"
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="px-6 pb-5 pt-2 gap-2 flex-col sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setPurchaseDialogOpen(false)}
              disabled={purchaseSubmitting}
              data-testid="purchase-cancel-btn"
              className="sm:mr-auto"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSubmitPurchaseDetails(true)}
              disabled={purchaseSubmitting}
              className="border-gray-300 text-gray-700 hover:bg-gray-50 text-left h-auto py-2 whitespace-normal"
              data-testid="purchase-skip-tech-sources-btn"
              title="Skip the research — the technician will source what's needed on-site"
            >
              <Wrench className="w-4 h-4 mr-2 shrink-0" />
              Items will be sourced by the technician on-site — no info available at the moment
            </Button>
            <Button
              onClick={() => handleSubmitPurchaseDetails(false)}
              disabled={purchaseSubmitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="purchase-requires-tech-btn"
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              {purchaseSubmitting ? "Saving…" : "Submit & Pick Assignee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalation Picker Dialog */}
      <Dialog open={escalateDialogOpen} onOpenChange={setEscalateDialogOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="escalate-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50">
            <DialogTitle className="flex items-center gap-2 text-orange-800 text-lg">
              <AlertTriangle className="w-5 h-5" />
              Escalate to Help Desk
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 py-5 space-y-5">
            <p className="text-sm text-gray-600 leading-relaxed">
              Pick a DO/DM for this market (the ticket auto-routes to the
              technician they manage) OR pick a specific technician directly.
            </p>

            {opsUsers.length > 0 && (
              <div className="space-y-2.5">
                <Label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                  <UserCheck className="w-4 h-4 text-indigo-600" />
                  Assign to a DO / DM
                </Label>
                <Select
                  value={selectedOpsUserId}
                  onValueChange={(v) => {
                    setSelectedOpsUserId(v);
                    setSelectedTechEmail("");
                  }}
                >
                  <SelectTrigger data-testid="escalate-ops-select" className="h-10">
                    <SelectValue placeholder="Select a DO / DM..." />
                  </SelectTrigger>
                  <SelectContent>
                    {opsUsers.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {o.full_name}{" "}
                            <span className="text-xs text-gray-500 font-normal">({o.role})</span>
                          </span>
                          <span className="text-[11px] text-gray-500">
                            {o.default_technician_email
                              ? `→ ${o.default_technician_name || o.default_technician_email}`
                              : "No default technician assigned"}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedOpsUserId && (() => {
                  const picked = opsUsers.find((o) => o.id === selectedOpsUserId);
                  if (!picked) return null;
                  if (picked.default_technician_email) {
                    return (
                      <div className="text-[12px] bg-indigo-50 border border-indigo-100 text-indigo-800 rounded-md px-3 py-2">
                        Will route to{" "}
                        <span className="font-semibold">
                          {picked.default_technician_name || picked.default_technician_email}
                        </span>{" "}
                        via {picked.full_name}.
                      </div>
                    );
                  }
                  return (
                    <div className="text-[12px] bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
                      {picked.full_name} has no default technician yet — set one in Admin Panel → Active Users, or pick a technician below.
                    </div>
                  );
                })()}
                <Button
                  onClick={() => {
                    const picked = opsUsers.find((o) => o.id === selectedOpsUserId);
                    if (!picked) return;
                    if (!picked.default_technician_email) {
                      toast.error(
                        `${picked.full_name} has no default technician set. Pick a technician directly below.`
                      );
                      return;
                    }
                    handleTroubleshootingComplete(false, picked.default_technician_email);
                  }}
                  disabled={
                    submitting ||
                    !selectedOpsUserId ||
                    !opsUsers.find((o) => o.id === selectedOpsUserId)?.default_technician_email
                  }
                  className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                  data-testid="assign-via-ops-btn"
                >
                  {submitting ? "Sending..." : "Route via DO/DM to their Technician"}
                </Button>
              </div>
            )}

            {opsUsers.length > 0 && marketTechs.length > 0 && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-[11px] uppercase">
                  <span className="bg-white px-3 text-gray-400 tracking-wider">or</span>
                </div>
              </div>
            )}

            {marketTechs.length > 0 && (
              <div className="space-y-2.5">
                <Label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                  <UserCheck className="w-4 h-4 text-indigo-600" />
                  Assign to a specific technician
                </Label>
                <Select
                  value={selectedTechEmail}
                  onValueChange={(v) => {
                    setSelectedTechEmail(v);
                    setSelectedOpsUserId("");
                  }}
                >
                  <SelectTrigger data-testid="escalate-tech-select" className="h-10">
                    <SelectValue placeholder="Select a technician..." />
                  </SelectTrigger>
                  <SelectContent>
                    {marketTechs.map((t) => (
                      <SelectItem key={t.email} value={t.email} data-testid={`tech-option-${t.email}`}>
                        {t.name ? `${t.name} — ${t.email}` : t.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={() => handleTroubleshootingComplete(false, selectedTechEmail)}
                  disabled={submitting || !selectedTechEmail}
                  variant="outline"
                  className="w-full h-10 border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400 font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  data-testid="assign-specific-tech-btn"
                >
                  {submitting ? "Sending..." : "Assign & Notify Selected Technician"}
                </Button>
              </div>
            )}

            {opsUsers.length === 0 && marketTechs.length === 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                No DOs/DMs or technicians available for this market yet. Configure them in <span className="font-medium">Admin Panel</span>.
              </div>
            )}

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-[11px] uppercase">
                <span className="bg-white px-3 text-gray-400 tracking-wider">or</span>
              </div>
            </div>

            <Button
              onClick={() => handleTroubleshootingComplete(false, null)}
              disabled={submitting}
              variant="outline"
              className="w-full h-10 border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400 font-medium"
              data-testid="assign-all-techs-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              Make available to all market technicians
            </Button>
          </div>
          <DialogFooter className="px-6 pb-5 pt-2">
            <Button variant="ghost" onClick={() => setEscalateDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
